import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, Link } from "react-router-dom";
import { Accordion, Button, Card, Dropdown } from "@amzn/alchemy-components-react";
import Excel from "exceljs";
import { logger } from "src/logger";
import { AlertType, PageType, TenantID, UserType } from "src/common/enums";
import { BreadCrumbs } from "src/components/breadcrumb";
import { PageProps } from "src/pages/page-interface";
import { AlertBar } from "src/components/alert-bar";
import ValidationResultRow from "src/components/validation-result-row";
import { FallbackSpinner } from "src/components/fallback-spinner";
import { useAuth } from "src/components/auth/auth-provider";
import { downloadExcelFile } from "src/common/excel-util";
import { getTranslatedErrorMsg, isEmailValid } from "src/common/util";
import { ENTERPRISE_SCOPE, NETWORK_ONLY, ONBOARD_URL, PRINCIPAL_LIST_PATH } from "src/common/constants";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_USER_INFO_BY_ALIAS_QUERY, CONFIRM_BULK_ADD_USER_BY_ALIAS_MUTATION } from "src/common/gql-operations";
import {
    ConfirmBulkAddUserByAliasData,
    ConfirmBulkAddUserByAliasVariables,
    ParsedAliasRow,
    ResolvedRow,
    FlaggedRow,
    classifyRows,
    buildConfirmEntries,
    AddByAliasValidationResult,
} from "src/models/bulk-add-user-by-alias";
import { ValidationError } from "src/models/validation-error";
import { parseSheet, SheetParseError } from "src/pages/principals/system-access-credential-sheet-parser";
import { buildTemplateWorkbook } from "src/pages/principals/system-access-credential-template-generator";
import initialMetricsPublisher from "src/metrics";
import * as KatalMetrics from "@amzn/katal-metrics";

type Step = "upload" | "preview";

const cloudWatchDimensions = [
    new KatalMetrics.Metric.String('page', 'bulk-add-by-system-access-credential'),
];
const additionalMetricsContext = new KatalMetrics.Context({ cloudWatchDimensions });
const graphqlClientMetricsPublisher =
    initialMetricsPublisher.newChildActionPublisherForMethod('graphql-client', additionalMetricsContext);

export const BulkAddBySystemAccessCredential = (props: PageProps) => {
    const { t } = useTranslation();
    const auth = useAuth();
    const history = useHistory();
    const location = useLocation();
    const inputRef = useRef<HTMLInputElement>(null);
    // Entry context comes from URL query params (?scope=<site>&from=users|onboard)
    // so the page is shareable and survives a refresh.
    const queryParams = new URLSearchParams(location.search);
    const incomingScope = queryParams.get("scope") ?? "";
    const cameFromOnboard = queryParams.get("from") === "onboard";
    // Where the "Done" button returns to — the referrer, defaulting to Users.
    const referrerPath = cameFromOnboard ? ONBOARD_URL : PRINCIPAL_LIST_PATH;

    const [scopeOptions, setScopeOptions] = useState<string[]>([]);
    const [selectedScope, setSelectedScope] = useState<string>("");
    const [scopesLoading, setScopesLoading] = useState<boolean>(true);

    const [file, setFile] = useState<File>();
    const [step, setStep] = useState<Step>("upload");
    // True once at least one batch has been added successfully this visit; reveals
    // the "Done" button so the user can return to where they came from.
    const [submittedSuccessfully, setSubmittedSuccessfully] = useState<boolean>(false);
    const [resolvedRows, setResolvedRows] = useState<ResolvedRow[]>([]);
    const [flaggedRows, setFlaggedRows] = useState<FlaggedRow[]>([]);

    const [alertType, setAlertType] = useState<AlertType>();
    const [alertHeader, setAlertHeader] = useState<string>();
    const [alertMsg, setAlertMsg] = useState<string>();
    const [validationResults, setValidationResults] = useState<AddByAliasValidationResult[]>();
    const [commonValidationErrors, setCommonValidationErrors] = useState<ValidationError[]>();

    const showAlert = (type: AlertType, header: string, msg: string) => {
        setAlertType(type); setAlertHeader(header); setAlertMsg(msg);
    };
    const resetAlert = () => { setAlertType(undefined); setAlertHeader(undefined); setAlertMsg(undefined); };
    const resetResults = () => {
        setResolvedRows([]);
        setFlaggedRows([]);
        setValidationResults(undefined);
        setCommonValidationErrors(undefined);
    };

    // Seed scopes from the user's access, Enterprise filtered out. Pre-select the
    // site passed in (e.g. from the principal list) when it's valid; otherwise
    // only auto-select when there's exactly one site. With multiple sites and no
    // incoming scope, leave it unselected so the user makes a deliberate choice.
    useEffect(() => {
        props.setActivePage(cameFromOnboard ? PageType.ONBOARD : PageType.PRINCIPAL_LIST);
        (async () => {
            const scopes = (await auth.getScopes()) ?? [];
            const usable = scopes.filter(s => s !== ENTERPRISE_SCOPE);
            setScopeOptions(usable);
            let seed = "";
            if (incomingScope && usable.includes(incomingScope)) {
                seed = incomingScope;
            } else if (usable.length === 1) {
                seed = usable[0];
            }
            setSelectedScope(seed);
            setScopesLoading(false);
        })();
    }, []);

    const [resolveAliases, { loading: resolving }] = useLazyQuery(GET_USER_INFO_BY_ALIAS_QUERY, {
        fetchPolicy: NETWORK_ONLY,
        onCompleted: (data) => {
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.SUCCESS', 1);
            const persons = data?.getUserInfoByAlias?.persons ?? [];
            const { resolved, flagged } = classifyRows(pendingRowsRef.current, persons);
            setResolvedRows(resolved);
            setFlaggedRows(flagged);
            if (resolved.length === 0) {
                showAlert(AlertType.error, t("submission-failed"), t("no-resolvable-users-found"));
                return;
            }
            setStep("preview");
        },
        onError: (error) => {
            logger.error("resolveAliases failed", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.ERROR', 1);
            showAlert(AlertType.error, t("submission-failed"), error.message);
        },
    });

    const [confirmAdd, { loading: confirming }] = useMutation<ConfirmBulkAddUserByAliasData, ConfirmBulkAddUserByAliasVariables>(
        CONFIRM_BULK_ADD_USER_BY_ALIAS_MUTATION, {
        onCompleted: (data) => {
            const result = data?.confirmBulkAddUserByAlias;
            setValidationResults(result?.validationResults ?? []);
            setCommonValidationErrors(result?.commonValidationErrors ?? []);
            const rowErrors = (result?.validationResults ?? []).reduce<ValidationError[]>((acc, r) => acc.concat(r.errors ?? []), []);
            if ((result?.successCount ?? 0) >= 1 && rowErrors.length === 0 && (result?.commonValidationErrors?.length ?? 0) === 0) {
                graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.SUCCESS', 1);
                showAlert(AlertType.success, t("success"), t("bulk-add-user-submitted"));
                setStep("upload");
                setFile(undefined);
                setSubmittedSuccessfully(true);
            } else {
                graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.VALIDATION_ERROR', 1);
                showAlert(AlertType.error, t("submission-failed"), t("please-correct-the-errors-and-resubmit-the-file"));
            }
        },
        onError: (error) => {
            logger.error("confirmBulkAddUserByAlias failed", error);
            graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.ERROR', 1);
            showAlert(AlertType.error, t("submission-failed"), error.message);
        },
    });

    // Holds parsed rows between the resolve invocation and its onCompleted (Apollo
    // callbacks capture stale closures; a ref reads the latest rows). Same pattern
    // the single modal uses for submittedAliasRef.
    const pendingRowsRef = useRef<ParsedAliasRow[]>([]);

    const handleTemplateDownload = async () => {
        const workbook = buildTemplateWorkbook(t);
        await downloadExcelFile(workbook, `fmui-bulk-add-user-template_${document.documentElement.lang}.xlsx`);
    };

    const openFilePicker = () => inputRef.current!.click();
    const onFileChosen = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) setFile(e.target.files[0]);
    };

    const handleSubmitUpload = async () => {
        resetAlert();
        resetResults();
        if (!selectedScope) { showAlert(AlertType.warning, t("submission-failed"), t("select-the-site-to-add-users-to")); return; }
        if (!file) { showAlert(AlertType.warning, t("no-file-selected"), t("please-select-a-file-and-try-again")); return; }
        try {
            const workbook = new Excel.Workbook();
            await workbook.xlsx.load(await file.arrayBuffer());
            const rows = parseSheet(workbook);
            pendingRowsRef.current = rows;
            graphqlClientMetricsPublisher.publishCounterMonitor('get-user-info-by-alias.INVOCATION', 1);
            resolveAliases({
                variables: {
                    getUserInfoByAliasInput: { tenantId: TenantID.AFTX, aliases: rows.map(r => r.alias) },
                },
            });
        } catch (e) {
            const msgKey = e instanceof SheetParseError ? e.message : "please-correct-the-errors-and-resubmit-the-file";
            logger.error("Failed to parse bulk add file", e as Error);
            showAlert(AlertType.error, t("submission-failed"), t(msgKey));
        }
    };

    const handleConfirm = () => {
        resetAlert();
        // Client-side email validation for 3P User Admin roles:
        // - Email must be present and valid
        // - Email must not be @amazon.com (3P users need a non-Amazon email)
        const emailErrors: AddByAliasValidationResult[] = [];
        resolvedRows.forEach(r => {
            if (r.userType === UserType.USER_ADMIN_PRIMARY || r.userType === UserType.USER_ADMIN_SECONDARY) {
                const rowErrors: ValidationError[] = [];
                if (!isEmailValid(r.email)) {
                    rowErrors.push({ translationKey: "apse-missing-email-for-3p" });
                } else if (r.email.trim().toLowerCase().endsWith("@amazon.com")) {
                    rowErrors.push({ translationKey: "apse-amazon-email-for-3p" });
                }
                if (rowErrors.length > 0) {
                    emailErrors.push({ rowNumber: r.rowNumber, alias: r.person.alias, userType: r.userType, errors: rowErrors });
                }
            }
        });
        if (emailErrors.length > 0) {
            setValidationResults(emailErrors);
            setCommonValidationErrors([]);
            showAlert(AlertType.error, t("submission-failed"), t("please-correct-the-errors-and-resubmit-the-file"));
            return;
        }
        // Clear only prior validation results, not the resolved/flagged rows: a
        // partial-failure confirm stays on the preview step, so the preview tables
        // must remain populated alongside the new error results.
        setValidationResults(undefined);
        setCommonValidationErrors(undefined);
        graphqlClientMetricsPublisher.publishCounterMonitor('add-principal-scope.INVOCATION', 1);
        confirmAdd({
            variables: {
                confirmBulkAddUserByAliasInput: {
                    scope: selectedScope,
                    tenantId: TenantID.AFTX,
                    users: buildConfirmEntries(resolvedRows),
                },
            },
        });
    };

    const handleBack = () => { setStep("upload"); resetAlert(); resetResults(); };

    // Returns to the referrer (Onboard menu or Users page).
    const handleDone = () => {
        logger.info(`Leaving bulk add users, returning to: ${referrerPath}`);
        history.push(referrerPath);
    };

    const loading = scopesLoading || resolving || confirming;

    // Derived: true when the last confirm returned per-row or common errors.
    // Used to disable the confirm button — user must go back and fix the file.
    const hasValidationErrors = (validationResults ?? []).some(r => r.errors.length > 0)
        || (commonValidationErrors ?? []).length > 0;

    return (
        <div id="bulk-add-user-page" className="container-fluid">
            <div className="row">
                <BreadCrumbs breadcrumbItems={[
                    cameFromOnboard
                        ? { tag: t("onboard"), path: ONBOARD_URL }
                        : { tag: t("users"), path: PRINCIPAL_LIST_PATH },
                    { tag: t("bulk-add-user-by-system-access-credential") },
                ]} />
            </div>
            <div className="row mx-0 mb-0-5 b-background">
                <div className="col title m-0 py-1">{t("bulk-add-user-by-system-access-credential")}</div>
            </div>

            {step === "upload" && (
                <div className="b-background">
                    <div className="row mx-0 px-2 pt-2 pb-2">
                        <div className="col">
                            <Accordion id="add-user-instructions" header={t("instructions")} expanded={true}>
                                <ol>
                                    <li>{t("click")}<Link id="add-user-template-link" to="#" onClick={handleTemplateDownload}>&nbsp;{t("here")}&nbsp;</Link>{t("bulk-add-user-instructions-step-1")}</li>
                                    <li>{t("bulk-add-user-instructions-step-2", { maxAddUserCount: 100 })}</li>
                                    <li>{t("bulk-add-user-instructions-step-3")}</li>
                                    <li>{t("bulk-add-user-instructions-step-4")}</li>
                                    <li>{t("bulk-add-user-instructions-step-5")}</li>
                                    <li>
                                        {t("click-on-the")}
                                        <b> {t("select-file")} </b>
                                        {t("bulk-add-user-instructions-step-6")}
                                    </li>
                                    <li>
                                        {t("click-on-the")}
                                        <b> {t("submit")} </b>
                                        {t("bulk-add-user-instructions-step-7")}
                                    </li>
                                    <li>{t("bulk-add-user-instructions-step-8")}</li>
                                    <li>{t("bulk-add-user-instructions-step-9")}</li>
                                </ol>
                            </Accordion>
                        </div>
                    </div>
                    <div className="row pb-4">
                        <div className="col">
                            <Card id="add-user-file-card" className="mx-auto px-4">
                                <div className="row pb-2">
                                    <div className="col">
                                        <Dropdown
                                            id="add-user-scope-select"
                                            label={t("select-the-site-to-add-users-to")}
                                            options={[
                                                { label: t("select-a-site"), value: "" },
                                                ...scopeOptions.map(s => ({ label: s, value: s })),
                                            ]}
                                            value={selectedScope}
                                            onChange={(e: any) => setSelectedScope(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="row pb-2">
                                    <div className="col-auto pr-0">
                                        <Button id="add-user-select-file" label={t("select-file")} onClick={openFilePicker} />
                                        <input ref={inputRef} id="add-user-input-file" type="file" accept=".xlsx" hidden onChange={onFileChosen} />
                                    </div>
                                    <div className="col"><p>{file ? file.name : t("no-file-selected")}</p></div>
                                </div>
                                <div className="row">
                                    <div className="col">
                                        <Button id="add-user-submit" className="c-success-alert" fullWidth={true} label={t("submit")} variant="action" disabled={resolving} onClick={handleSubmitUpload} />
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                    {submittedSuccessfully && (
                        <div className="row pb-4">
                            <div className="col d-flex justify-content-end">
                                <Button id="add-user-done" label={t("close")} onClick={handleDone} />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {step === "preview" && (
                <div className="b-background px-2 pb-3">
                    <div className="row"><div className="col"><p>{t("bulk-add-user-preview-header")}</p></div></div>
                    <div className="row"><div className="col">
                        <h5>{t("bulk-add-user-resolved-section-header")}</h5>
                        <table className="alchemy-table bordered" style={{ width: "100%" }}>
                            <thead><tr>
                                <th>{t("system-access-credential")}</th><th>{t("name")}</th><th>{t("job-type")}</th><th>{t("user-type")}</th><th>{t("email")}</th>
                            </tr></thead>
                            <tbody>
                                {resolvedRows.map((r) => (
                                    <tr key={r.rowNumber}>
                                        <td>{r.person.alias}</td>
                                        <td>{r.person.firstName} {r.person.lastName}</td>
                                        <td>{r.person.jobType}</td>
                                        <td>{r.userType}</td>
                                        <td>{r.email}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div></div>

                    {flaggedRows.length > 0 && (
                        <div className="row mt-3"><div className="col">
                            <h5>{t("bulk-add-user-flagged-section-header")}</h5>
                            <table className="alchemy-table bordered" style={{ width: "100%" }}>
                                <thead><tr><th>{t("bulk-add-user-excel-row-number")}</th><th>{t("system-access-credential")}</th><th>{t("bulk-add-user-reason")}</th></tr></thead>
                                <tbody>
                                    {flaggedRows.map((r) => (
                                        <tr key={r.rowNumber}><td>{r.rowNumber}</td><td>{r.alias}</td><td>{t(r.reasonKey)}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div></div>
                    )}

                    <div className="row d-flex justify-content-center modal-button-row mt-3">
                        <Button id="add-user-back" label={t("back")} onClick={handleBack} className="mx-1" />
                        <Button id="add-user-confirm" label={t("confirm")} variant="action" disabled={confirming || hasValidationErrors || flaggedRows.length > 0} onClick={handleConfirm} className="mx-1" />
                    </div>
                </div>
            )}

            {commonValidationErrors && commonValidationErrors.length > 0 && (
                <div className="row px-2 pb-2"><div className="col"><table className="alchemy-table bordered">
                    <thead><tr><th>{t("common-validation-errors")}</th></tr></thead>
                    <tbody><tr><td>
                        <Accordion header={`${commonValidationErrors.length} ${t("common-validation-errors-expand-to-view-details")}`} transparent>
                            <ul>{commonValidationErrors.map((err, i) => <li key={i}>{getTranslatedErrorMsg(t, err)}</li>)}</ul>
                        </Accordion>
                    </td></tr></tbody>
                </table></div></div>
            )}

            {validationResults && validationResults.some(item => item.errors.length > 0) && (
                <div className="row px-2 pb-2"><div className="col"><table className="alchemy-table bordered">
                    <thead><tr><th>{t("bulk-add-user-excel-row-number")}</th><th>{t("bulk-onboard-results-table-col-header-validation-errors")}</th></tr></thead>
                    <tbody>
                        {validationResults.map((item) => item.errors.length > 0 ? (
                            <ValidationResultRow key={item.rowNumber} excelRowNum={item.rowNumber} validationErrors={item.errors} />
                        ) : null)}
                    </tbody>
                </table></div></div>
            )}

            {loading && <FallbackSpinner />}
            {alertMsg && <AlertBar id="add-user-alert" result={alertType!} dismissible={true} header={alertHeader!} message={alertMsg!} reset={resetAlert} />}
        </div>
    );
};

export default BulkAddBySystemAccessCredential;
